import { useQuery } from '@tanstack/react-query';
import { ChangeEvent, ReactNode, useMemo, useState } from 'react';

import { _getCategories } from '@/api/address-book/queries';
import { _getFile } from '@/api/files/queries';
import { _getCountries } from '@/api/lookup/queries';
import { LocationList } from '@/api/lookup/types';
import EditIcon from '@/assets/svg/ui-elements/edit-outline.svg';
import GlobeIcon from '@/assets/svg/ui-elements/globe.svg';
import LinkIcon from '@/assets/svg/ui-elements/link.svg';
import MailIcon from '@/assets/svg/ui-elements/mail.svg';
import PhoneIcon from '@/assets/svg/ui-elements/phone.svg';
import { flattenCategoryChildren, getCategoryVariant } from '@/pages/address-book/utils';
import { Avatar, Badge, Drawer, TextArea, theme, Typography } from '@/shared/design-system';

import { PLATFORM_ICONS } from '../../components/social-profiles-block';
import type { ContactData } from '../../types';
import {
  BadgeRow,
  LogoImage,
  ReviewSections,
  RowLabel,
  RowValue,
  SummaryCard,
  SummaryHeader,
  SummaryRow,
  SummaryRows,
} from './styled';

interface SummaryEntry {
  key: string;
  label: string;
  value: string;
  icon: ReactNode;
}

interface ReviewSaveStepProps {
  data: Partial<ContactData>;
  categoryIds: string[];
  canEditCategories: boolean;
  isSubmitting?: boolean;
  onSave: (_notes: string) => void;
  onEditCategories: () => void;
  onBack: () => void;
  onClose: () => void;
}

export const ReviewSaveStep = ({
  data,
  categoryIds,
  canEditCategories,
  isSubmitting,
  onSave,
  onEditCategories,
  onBack,
  onClose,
}: ReviewSaveStepProps) => {
  const [notes, setNotes] = useState(data.notes ?? '');

  const { data: countries = [] } = useQuery(
    _getCountries({ list: LocationList.ADDRESS, includeRegions: true }),
  );
  const { data: categories = [] } = useQuery(_getCategories());
  const { data: iconFile } = useQuery(_getFile(data.iconId, true));

  const countryNameOf = (countryId: string | null | undefined) =>
    countries.find((country) => country.id === countryId)?.name ?? countryId ?? '';

  const entries = useMemo<SummaryEntry[]>(() => {
    const rows: SummaryEntry[] = [];

    (data.emails ?? []).filter(Boolean).forEach((email, index) => {
      rows.push({ key: `email-${index}`, label: 'Email', value: email, icon: <MailIcon /> });
    });

    (data.phones ?? []).filter(Boolean).forEach((phone, index) => {
      rows.push({ key: `phone-${index}`, label: 'Phone', value: phone, icon: <PhoneIcon /> });
    });

    (data.addresses ?? []).forEach((address, index) => {
      const value = [countryNameOf(address.country), address.city, address.street, address.street2]
        .filter(Boolean)
        .join(', ');
      if (value) {
        rows.push({
          key: `address-${index}`,
          label: 'Residence',
          value,
          icon: <GlobeIcon />,
        });
      }
    });

    (data.socialLinks ?? [])
      .filter((link) => link.value.trim())
      .forEach((link, index) => {
        const PlatformIcon = PLATFORM_ICONS[link.platform];
        rows.push({
          key: `social-${index}`,
          label: link.platform,
          value: link.value,
          icon: PlatformIcon ? <PlatformIcon /> : <LinkIcon />,
        });
      });

    (data.websites ?? []).filter(Boolean).forEach((website, index) => {
      rows.push({ key: `website-${index}`, label: 'Website', value: website, icon: <LinkIcon /> });
    });

    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, countries]);

  const selectedCategories = useMemo(() => {
    const selected = new Set(categoryIds);
    return categories.flatMap((root) =>
      flattenCategoryChildren(root.children)
        .filter(({ node }) => selected.has(node.id))
        .map(({ node }) => ({ id: node.id, name: node.name, rootName: root.name })),
    );
  }, [categories, categoryIds]);

  return (
    <Drawer
      open
      onOpenChange={(isOpen) => !isOpen && onClose()}
      title="Review & Save"
      description="Confirm the contact information before saving"
      maxWidth={600}
      onBack={onBack}
      leftAction={
        canEditCategories
          ? {
              children: (
                <>
                  <EditIcon width={16} height={16} />
                  Edit categories
                </>
              ),
              onClick: onEditCategories,
            }
          : undefined
      }
      secondaryAction={{ children: 'Cancel', color: 'tertiary', onClick: onClose }}
      primaryAction={{
        children: 'Save',
        onClick: () => onSave(notes),
        disabled: isSubmitting,
      }}
    >
      <ReviewSections>
        <SummaryCard>
          <SummaryHeader>
            {iconFile?.url ? (
              <LogoImage src={iconFile.url} alt="" aria-hidden />
            ) : (
              <Avatar size="lg" initials={data.name ?? ''} />
            )}
            <Typography variant="h6" as="h3">
              {data.name}
            </Typography>
          </SummaryHeader>

          {entries.length > 0 && (
            <SummaryRows>
              {entries.map((entry) => (
                <SummaryRow key={entry.key}>
                  <RowLabel>
                    {entry.icon}
                    <Typography variant="body3">{entry.label}</Typography>
                  </RowLabel>
                  <RowValue>
                    <Typography variant="body3">{entry.value}</Typography>
                  </RowValue>
                </SummaryRow>
              ))}
            </SummaryRows>
          )}
        </SummaryCard>

        {selectedCategories.length > 0 && (
          <div>
            <Typography variant="body3" color={theme.colors.text.secondary}>
              Categories ({selectedCategories.length})
            </Typography>
            <BadgeRow style={{ marginTop: 8 }}>
              {selectedCategories.map((category) => (
                <Badge
                  key={category.id}
                  label={category.name}
                  color={getCategoryVariant(category.rootName)}
                  variant="subtle"
                  size="md"
                />
              ))}
            </BadgeRow>
          </div>
        )}

        <TextArea
          label="Add Notes"
          optional
          rows={3}
          placeholder="Add a note about this contact"
          value={notes}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setNotes(event.target.value)}
        />
      </ReviewSections>
    </Drawer>
  );
};
